import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { RoleRightsService } from "./role.rights.service";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { S25SimpleCollapseModule } from "../../s25-simple-collapse/s25.simple.collapse.module";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { MultiselectModelI } from "../../s25-multiselect/s25.multiselect.component";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { AccessLevel, AccessLevels } from "../../../pojo/Fls";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25Util } from "../../../util/s25-util";
import { RoleRights } from "./role.rights";
import RoleWithAccessLevel = RoleRights.RoleWithAccessLevel;
import RightWithRolesConfig = RoleRights.RightWithRolesConfig;
import { MultiselectResultsApi } from "../../s25-multiselect/s25.multiselect.results.component";

/**
 * Configuration structure grouping rights by category
 * Top level keys are category names (e.g., "Members", "Budget")
 * Each category maps right names to their configuration details
 */
export type CategoryRightsConfig = {
    [categoryName: string]: {
        [rightName: string]: RightConfigWithMultiselect;
    };
};

/**
 * A single right with its definition and multiselect models
 */
export type RightConfigWithMultiselect = RoleRights.RightDefinition & {
    read?: MultiselectModelI;
    full: MultiselectModelI;
};

@TypeManagerDecorator("s25-ng-se-security")
@Component({
    selector: "s25-ng-se-security",
    template: `
        <div class="se-security-config">
            <div class="c-padding--double">
                <h1>Student Engagement Security Configuration</h1>
                <p class="description">
                    Configure which contact roles have access to specific rights within Student Engagement. Changes are
                    saved automatically.
                </p>

                @if (loading()) {
                    <s25-loading-inline [model]="{ text: 'Loading configuration...' }" />
                }

                @if (config()) {
                    @if (saving()) {
                        <div class="save-indicator">Saving...</div>
                    }

                    @for (category of config() | keyvalue; track category.key) {
                        <div class="category-section card">
                            <s25-simple-collapse [headerText]="category.key" [defaultCollapsed]="false">
                                <div class="category-content">
                                    @for (rightWithRoles of category.value | keyvalue; track rightWithRoles.key) {
                                        <div class="right-item">
                                            <h4 class="right-name">{{ rightWithRoles.value.displayName }}</h4>
                                            <div class="includeExclude">
                                                <div class="read-access">
                                                    <h3>{{ rightWithRoles.value.readDescription }}</h3>
                                                    <s25-ng-multiselect-search-criteria
                                                        [type]="'organizationRoles'"
                                                        [modelBean]="rightWithRoles.value.read"
                                                        [selectedItems]="rightWithRoles.value.read.selectedItems"
                                                        [popoverOnBody]="true"
                                                        [showResults]="true"
                                                        [useSecurity]="false"
                                                        (onDone)="
                                                            onRolesChange(
                                                                rightWithRoles.value,
                                                                READ_ACCESS_LEVEL,
                                                                $event
                                                            )
                                                        "
                                                    />
                                                </div>
                                                <div class="full-access">
                                                    <h3>{{ rightWithRoles.value.description }}</h3>
                                                    <s25-ng-multiselect-search-criteria
                                                        [type]="'organizationRoles'"
                                                        [modelBean]="rightWithRoles.value.full"
                                                        [selectedItems]="rightWithRoles.value.full.selectedItems"
                                                        [popoverOnBody]="true"
                                                        [showResults]="true"
                                                        [useSecurity]="false"
                                                        (onDone)="
                                                            onRolesChange(
                                                                rightWithRoles.value,
                                                                FULL_ACCESS_LEVEL,
                                                                $event
                                                            )
                                                        "
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </s25-simple-collapse>
                        </div>
                    }
                }
            </div>
        </div>
    `,
    styles: `
        .se-security-config {
            .description {
                color: #666;
                margin-bottom: 2rem;
            }

            .save-indicator {
                position: fixed;
                top: 20px;
                right: 20px;
                background: #007bff;
                color: white;
                padding: 0.75rem 1.5rem;
                border-radius: 4px;
                font-weight: 600;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                z-index: 1000;
            }

            .card {
                background: white;
                border: 1px solid #ddd;
                border-radius: 8px;
                padding: 1.5rem;
                margin-bottom: 1.5rem;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            }

            .category-section {
                .category-content {
                    padding-top: 0.5rem;
                }

                .right-item {
                    padding: 1.5rem;
                    border: 1px solid #e0e0e0;
                    border-radius: 6px;
                    margin-bottom: 1rem;
                    background: #fafafa;

                    &:last-child {
                        margin-bottom: 0;
                    }

                    .right-name {
                        margin: 0 0 1rem 0;
                        font-size: 1.1rem;
                        font-weight: 600;
                        color: #222;
                    }

                    .includeExclude {
                        display: flex;
                    }

                    ::ng-deep .nm-party--on & .includeExclude {
                        color: white;
                    }

                    .includeExclude > div {
                        width: 50%;
                    }

                    .includeExclude .read-access {
                        padding-right: 1em;
                    }

                    .includeExclude .full-access {
                        border-left: 1px solid rgba(0, 0, 0, 0.15);
                        padding-left: 1em;
                    }

                    .includeExclude h3 {
                        text-align: center;
                        font-weight: bold;
                        font-size: 0.95rem;
                        color: #666;
                        margin-bottom: 1rem;
                    }

                    .includeExclude s25-ng-multiselect-search-criteria {
                        text-align: center;
                    }
                }
            }
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25SimpleCollapseModule, S25MultiselectModule, S25LoadingInlineModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeSecurityComponent {
    /** Component summary
     *
     * Fetches all the roles in the system and their assignments to each right.
     *
     * Each right has a section in the UI.
     * All the roles in 'R' are displayed in the left multiselect
     * All the roles in 'F' are displayed in the right multiselect
     * 'N' is not displayed in the UI indicating no access
     *
     * 'C' is currently not supported
     *
     * Adding a role to a right sets the acceess level for that role to 'F' or 'R' depending on the multiselect used. This should remove the role from the other multiselect.
     *
     *
     * On init I'll sort each role into the correct multiselect based on the access level and right name.
     *
     *
     */

    protected loading = signal(true);
    protected error = signal<string | null>(null);
    protected config = signal<CategoryRightsConfig | null>(null);
    protected saving = signal(false);

    constructor(
        private cd: ChangeDetectorRef,
        private el: ElementRef,
    ) {
        effect(() => {
            void this.loadConfig();
        });
    }

    splitByPrivilege(roles: RoleWithAccessLevel[]): { read: RoleWithAccessLevel[]; full: RoleWithAccessLevel[] } {
        return {
            read: roles.filter((role) => role.accessLevel === AccessLevels.Read),
            full: roles.filter((role) => role.accessLevel === AccessLevels.Full),
        };
    }

    createMultiSelectBeans(right: RightWithRolesConfig): { read?: MultiselectModelI; full: MultiselectModelI } {
        const splitRoles = this.splitByPrivilege(right.roles);

        const full: RightWithRolesConfig = {
            rightName: right.rightName,
            displayName: right.displayName,
            category: right.category,
            description: right.description,
            roles: splitRoles.full,
        };

        const read: RightWithRolesConfig = {
            rightName: right.rightName,
            displayName: right.displayName,
            category: right.category,
            description: right.readDescription || "",
            roles: splitRoles.read,
        };

        return {
            read: this.getMultiselectBean(read, AccessLevels.Read),
            full: this.getMultiselectBean(full, AccessLevels.Full),
        };
    }

    protected async loadConfig() {
        const config = await RoleRightsService.getRightsConfig();

        //BuildingSomething like this:
        const newCategories: CategoryRightsConfig = {};

        config.forEach((permission) => {
            const category = permission.category;

            if (!newCategories[category]) {
                newCategories[category] = {};
            }

            newCategories[category][permission.rightName] = {
                rightName: permission.rightName,
                displayName: permission.displayName,
                category: permission.category,
                description: permission.description,
                readDescription: permission.readDescription || "",
                ...this.createMultiSelectBeans(permission),
            };
        });

        this.config.set(newCategories);
    }

    getMultiselectBean(right: RightWithRolesConfig, accessLevel: AccessLevel): MultiselectModelI {
        return {
            uuid: `${right.rightName}-${accessLevel}`,
            title: "Roles",
            hasFilter: true,
            hasSelectAll: true,
            hasSelectNone: true,
            showResult: true,
            selectedItems: right.roles.map((role: RoleWithAccessLevel) => ({
                itemId: role.roleId,
                itemName: role.roleName,
            })),
            removeAction: (item: S25ItemI) => {
                if (item.skipDelete) {
                    item.skipDelete = false; //we're just syncing, don't save but reset this flag
                    return;
                }
                if (!!item.itemId) this.saveRight(right.rightName, new Map([[+item.itemId, AccessLevels.None]]));
            },
        };
    }

    /*
    Makes sure that both a role is only in one multiselect per right
    Selected items take priority
     */
    protected syncRoles(
        rightWithRoles: RightConfigWithMultiselect,
        accessLevel: AccessLevel,
        selectedItems: S25ItemI[],
    ) {
        //Determine which list to change - this is the opposite of the access level being set
        const listToChange = accessLevel === AccessLevels.Full ? rightWithRoles.read : rightWithRoles.full;

        //Remove any selected items from the other list
        if (listToChange && listToChange.selectedItems) {
            const needsAttention = S25Util.array.shallowIntersection(listToChange.selectedItems, selectedItems, [
                "itemId",
            ]);
            needsAttention.forEach((item: S25ItemI) => {
                listToChange?.removeSelectedItem?.({ ...item, skipDelete: true });
            });
            // Force update the multiselect results display, only if uuid is defined
            if (listToChange?.uuid) {
                void MultiselectResultsApi.refresh(this.el.nativeElement, listToChange.uuid);
            }
        }

        this.cd.detectChanges();
    }

    protected onRolesChange(right: RightConfigWithMultiselect, accessLevel: AccessLevel, selectedItems: S25ItemI[]) {
        this.syncRoles(right, accessLevel, selectedItems);
        this.saveRight(right.rightName, this.buildRoleAssignments(right, accessLevel));
    }

    buildRoleAssignments(right: RightConfigWithMultiselect, accessLevel: AccessLevel): Map<number, AccessLevel> {
        const roleAssignments = new Map<number, AccessLevel>();
        const listToChange = accessLevel === AccessLevels.Full ? right.full : right.read;

        listToChange?.selectedItems?.forEach((item: S25ItemI) => {
            roleAssignments.set(item.itemId as number, accessLevel);
        });
        return roleAssignments;
    }

    private async saveRight(rightName: string, roleAssignments: Map<number, AccessLevel>) {
        if (this.saving()) return;

        this.saving.set(true);
        this.error.set(null);

        try {
            await RoleRightsService.saveRolesForRight(rightName, roleAssignments);
        } catch (err) {
            this.error.set("Failed to save configuration. Please try again.");
            console.error("Error saving right configuration:", err);
        } finally {
            this.saving.set(false);
        }
    }

    //For use in the template to avoid magic strings
    protected READ_ACCESS_LEVEL = AccessLevels.Read;
    protected FULL_ACCESS_LEVEL = AccessLevels.Full;
}
